import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useBlocker } from "@tanstack/react-router";

// The Availability editor's unsaved-edit guard (NO-878): every router
// navigation (tab, RD switch, rail link) goes through TanStack's blocker, a
// full unload through beforeunload, and the editor's own in-view moves (mode,
// week arrows) through `guard`. All three share one dialog. The dirty flag is
// read from a ref so a confirmed move can clear it synchronously and not meet
// the blocker a second time.

export interface UnsavedGuard {
  /** Whether the "Discard unsaved changes?" dialog is up. */
  open: boolean;
  /** Run `action` now when clean, or after the dialog's Discard. */
  guard: (action: () => void) => void;
  confirm: () => void;
  cancel: () => void;
}

export function useUnsavedGuard(dirty: boolean): UnsavedGuard {
  const dirtyRef = useRef(dirty);
  const pending = useRef<(() => void) | null>(null);
  const settled = useRef(false);
  const [asked, setAsked] = useState(false);

  useLayoutEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const blocker = useBlocker({
    shouldBlockFn: () => dirtyRef.current,
    enableBeforeUnload: () => dirtyRef.current,
    withResolver: true,
  });
  const blocked = blocker.status === "blocked";

  const guard = useCallback((action: () => void) => {
    if (!dirtyRef.current) {
      action();

      return;
    }

    pending.current = action;
    setAsked(true);
  }, []);

  function confirm() {
    settled.current = true;
    setAsked(false);
    dirtyRef.current = false;

    if (blocked) {
      blocker.proceed();

      return;
    }

    const action = pending.current;
    pending.current = null;
    action?.();
  }

  function cancel() {
    // The dialog's Discard button also reports a close; that one is settled.
    if (settled.current) {
      settled.current = false;

      return;
    }

    setAsked(false);
    pending.current = null;

    if (blocked) blocker.reset();
  }

  return { open: asked || blocked, guard, confirm, cancel };
}
